import { Space } from 'antd';
import type { Rule } from 'antd/es/form';
import type { CSSProperties } from 'react';
import { useMemo } from 'react';
import { type StrictOmit, Unit, UNIT_TRANSLATED } from '@enmon/commons';
import { FormattedMessage } from '@/components/formatted_message/FormattedMessage';
import { InputNumber, type InputNumberProps } from '@/components/form/input/InputNumber';
import { Select, type SelectProps } from '@/components/form/Select/Select';
import { EmptyCell } from '@/components/table/cell';
import { NUMBER_WITH_UNIT_VALIDATION_MESSAGES } from './messages';

export interface NumberWithUnitInputValue {
  value?: number | null;
  unit?: Unit | null;
}

/**
 * `Form.Item normalize` for a NumberWithUnitInput: a fully-cleared field becomes `null` so the backend
 * rewrites it to null instead of rejecting an invalid `{ value: null, unit: null }`.
 */
export const normalizeNumberWithUnit = (
  value: NumberWithUnitInputValue | null | undefined,
): NumberWithUnitInputValue | null => (value && (value.value != null || value.unit != null) ? value : null);

/**
 * `Form.Item rules` entry for a NumberWithUnitInput: value and unit are all-or-nothing. An empty field is
 * optional, but once either half is filled the other becomes required. The validator rejects without a
 * message so antd surfaces the rule's own localized `message`.
 */
export const numberWithUnitRule: Rule = {
  message: <FormattedMessage {...NUMBER_WITH_UNIT_VALIDATION_MESSAGES.incomplete} />,
  validator: (_rule, value: NumberWithUnitInputValue | null | undefined) => {
    const hasValue = value?.value != null;
    const hasUnit = value?.unit != null;
    return hasValue === hasUnit ? Promise.resolve() : Promise.reject();
  },
};

export interface NumberWithUnitInputProps {
  value?: NumberWithUnitInputValue;
  onChange?: (value: NumberWithUnitInputValue) => void;
  units?: readonly Unit[];
  disabled?: boolean;
  /**
   * When false (default) the unit stays empty until picked and is clearable, so an untouched field isn't
   * mutated into carrying a unit it never had. Set true for mandatory fields to default the unit to the
   * first allowed one.
   */
  required?: boolean;
  style?: CSSProperties;
  inputNumberProps?: StrictOmit<InputNumberProps, 'value' | 'onChange'>;
  selectProps?: StrictOmit<SelectProps<Unit>, 'value' | 'onChange' | 'options'>;
  'data-cy'?: string;
}

export const NumberWithUnitInput = ({
  value,
  onChange,
  units = Object.values(Unit),
  disabled,
  required = false,
  style,
  inputNumberProps,
  selectProps,
  'data-cy': dataCy,
}: NumberWithUnitInputProps) => {
  const unitOptions = useMemo(
    () =>
      units.map(unit => ({
        value: unit,
        label: UNIT_TRANSLATED[unit],
      })),
    [units],
  );

  const defaultUnit = required ? units[0] : undefined;
  const currentUnit = value?.unit ?? defaultUnit;

  const unitDefaultPlaceholder = <EmptyCell />;

  const handleValueChange: InputNumberProps['onChange'] = newValue => {
    onChange?.({ value: (newValue as number | null) ?? null, unit: currentUnit ?? null });
  };

  const handleUnitChange: SelectProps<Unit>['onChange'] = newUnit => {
    onChange?.({ value: value?.value ?? null, unit: newUnit ?? defaultUnit ?? null });
  };

  return (
    <Space.Compact block style={style} data-cy={dataCy}>
      <InputNumber
        value={value?.value}
        onChange={handleValueChange}
        disabled={disabled}
        style={{ width: '100%' }}
        data-cy={dataCy ? `${dataCy}-value` : undefined}
        {...inputNumberProps}
      />
      <Select<Unit>
        allowClear={!required}
        value={currentUnit}
        onChange={handleUnitChange}
        options={unitOptions}
        placeholder={unitDefaultPlaceholder}
        disabled={disabled}
        showSearch={false}
        styles={{ placeholder: { width: '100%' } }}
        style={{ width: 120 }}
        data-cy={dataCy ? `${dataCy}-unit` : undefined}
        {...selectProps}
      />
    </Space.Compact>
  );
};
